import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import { AvailableTimePicker } from '@wsb/ola-widget-components';
import AvailableTimeSelectionStore from '../stores/AvailableTimeSelection';
import ServiceListStore from '../stores/ServiceList';
import ConfigStore from '../stores/Config';
import FormattersStore from '../stores/Formatters';
import {
  fetchAvailableTimes,
  selectAvailableTime,
  fetchAvailableDates,
  selectResource
} from '../actions/AvailableTimeSelection';
import { scrollOlaWidget } from '../actions/ScrollWidgetActions';
import { Fetching } from './Fetching';
import ServiceInfo from './ServiceInfo';
import views from '../constants/views';
import InViewportWrapper from '../wrappers/InViewportWrapper';
import renderModes from '../constants/renderModes';
import BookButtonContainer from './BookButtonContainer';
import dataAids from '../constants/dataAids';
import { trackSingle } from '../../common/utils/FacebookPixel';
import { trackMetric } from '../../common/utils/trackMetric';
import { trackPixel } from '../../common/utils/FacebookPixel';
import { isExperimentEnabled } from '../../common/utils/hiveMind';
import {
  AVAILABLE_TIMES_SELECTION_IMPRESSION,
  AVAILABLE_TIMES_SELECTION_BOOK_CTA_CLICK,
  AVAILABLE_TIMES_SELECTION_CALENDAR_DAY_CLICK,
  AVAILABLE_TIMES_SELECTION_AVAILABLE_TIME_CLICK
} from '../constants/metrics';
import SocialSharing from './SocialSharing';
import {
  OLA_ADDON,
  OLA_ADDON_PHASE_II,
  OLA_BOOK_BUTTON_PROMINENT,
  OLA_BOOKING_SERVICE_LOCATION
} from '../../constants/experiments';
import AddonMenu from './AddonMenu';

const styles = {
  grid: {
    '@xs': {
      flexWrap: 'wrap-reverse'
    },
    '@md': {
      flexWrap: 'wrap'
    }
  },
  separatorHorizontal: {
    '@sm': {
      display: 'none'
    }
  },
  serviceStyles: {
    block: {
      '@xs': {
        borderBottomWidth: '1px',
        borderColor: 'input',
        borderBottomStyle: 'solid',
        marginBottom: 'medium',
        paddingBottom: 'medium',
        '@xs-only': {
          marginBottom: '0'
        }
      },
      '@sm': {
        borderWidth: '0px'
      }
    },
    name: {
      marginBottom: 'xsmall',
      wordWrap: 'break-word',
      '@xs-only': {
        marginBottom: 'xxsmall'
      }
    },
    durationAndCost: {
      marginBottom: 'medium',
      '@xs-only': {
        marginBottom: 'xxsmall'
      }
    }
  },
  leftColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  availabilityPickerContainer: {
    width: '100%'
  }
};

export default class AvailableTimeSelection extends Component {
  constructor(props) {
    super(props);
    this.state = this.getStoreState();
    this.onStoreChange = this.onStoreChange.bind(this);
    this.onResourceClick = this.onResourceClick.bind(this);
    this.isOlaBookButtonProminentExperimentEnabled = isExperimentEnabled.bind(
      this,
      ConfigStore,
      OLA_BOOK_BUTTON_PROMINENT
    );
    this.isOlaBookingServiceLocationExperimentEnabled = isExperimentEnabled.bind(
      this,
      ConfigStore,
      OLA_BOOKING_SERVICE_LOCATION
    );
    this.isOlaAddonExperimentEnabled = isExperimentEnabled.bind(this, ConfigStore, OLA_ADDON);
    this.isOlaAddonPhaseIIExperimentEnabled = isExperimentEnabled.bind(
      this,
      ConfigStore,
      OLA_ADDON_PHASE_II
    );
  }

  getStoreState() {
    const state = AvailableTimeSelectionStore.getState();
    const { selectedService } = ServiceListStore.getState();
    const {
      timeZone,
      max_future_calendar_months: maxFutureCalendarMonths,
      websiteId
    } = ConfigStore.getState();

    return {
      ...state,
      selectedService,
      timeZone,
      maxFutureCalendarMonths,
      websiteId
    };
  }

  componentDidMount() {
    AvailableTimeSelectionStore.addListener('change', this.onStoreChange);
    ServiceListStore.addListener('change', this.onStoreChange);
    ConfigStore.addListener('change', this.onStoreChange);

    this.trackFBPixelEvent();
    this.trackViewPixel();
    scrollOlaWidget();
    selectAvailableTime(null);
    trackMetric(AVAILABLE_TIMES_SELECTION_IMPRESSION);
  }

  componentWillUnmount() {
    AvailableTimeSelectionStore.removeListener('change', this.onStoreChange);
    ServiceListStore.removeListener('change', this.onStoreChange);
    ConfigStore.removeListener('change', this.onStoreChange);
  }

  onStoreChange() {
    this.setState(this.getStoreState());
  }

  trackViewPixel() {
    const { selectedService } = this.state;
    const data = {
      content_name: selectedService.name,
      content_type: 'service'
    };
    trackPixel('ViewContent', data);
  }

  trackFBPixelEvent() {
    const { websiteId } = this.state;
    const { olaFbPixelId, vnextFbPixelId } = this.props;

    trackSingle({
      custom: true,
      event: 'LDP_PageView',
      pixelId: olaFbPixelId,
      vnextFbPixelId: vnextFbPixelId,
      data: {
        business_id: websiteId
      }
    });
  }

  onResourceClick() {
    scrollOlaWidget();
  }

  onClickBook = () => {
    trackMetric(AVAILABLE_TIMES_SELECTION_BOOK_CTA_CLICK);
    this.props.navigate(views.BOOKING_FORM);
  };

  get bookButtonText() {
    const { staticContent } = this.props;
    const { selectedService } = this.state;

    // Gopay cart is OFF
    if (!ConfigStore.isGopayCartOn()) {
      return this.isOlaBookButtonProminentExperimentEnabled()
        ? staticContent.bookNow
        : staticContent.book;
    }

    if (selectedService.location_type === 'customer') {
      return staticContent.continue;
    }

    return this.isOlaBookButtonProminentExperimentEnabled()
      ? staticContent.bookNow
      : staticContent.book;
  }

  setBookButtonStyles(currentViewDevice) {
    if (currentViewDevice) {
      // PUBLISH side is null here
      // Only show button in the view that was last clicked
      const isMobilePreview = currentViewDevice === 'MOBILE_RENDER_DEVICE';
      const isTabletPreview = currentViewDevice === 'TABLET_RENDER_DEVICE';

      return {
        marginBottom: this.isOlaBookButtonProminentExperimentEnabled() ? 'xsmall' : '',
        marginTop: this.isOlaBookButtonProminentExperimentEnabled() ? 'xsmall' : '',
        display: isMobilePreview ? 'block' : 'none',
        '@sm': {
          display: isTabletPreview ? 'block' : 'none'
        }
      };
    }

    return {
      marginBottom: this.isOlaBookButtonProminentExperimentEnabled() ? 'xsmall' : '',
      marginTop: this.isOlaBookButtonProminentExperimentEnabled() ? 'xsmall' : ''
    };
  }

  onDayClick = () => {
    trackMetric(AVAILABLE_TIMES_SELECTION_CALENDAR_DAY_CLICK);
  };

  onAvailableTimeClick = () => {
    trackMetric(AVAILABLE_TIMES_SELECTION_AVAILABLE_TIME_CLICK);
  };

  render() {
    const {
      selectedService,
      timeZone,
      maxFutureCalendarMonths,
      selectedAvailableTime,
      currentViewDevice
    } = this.state;

    const {
      section,
      staticContent,
      viewDevice,
      renderMode,
      showFacebook,
      showTwitter,
      isOlaStrikeThroughPriceExperimentEnabled
    } = this.props;

    const { Block, HR } = UX2.Element;
    const {
      Grid,
      Grid: { Cell }
    } = UX2.Component;
    const { dateTimeFormatter } = FormattersStore.getState();

    return (
      <InViewportWrapper options={{ rootMargin: '2px 0px' }}>
        { ({ ref, isInView }) => (
          <div ref={ ref }>
            <Block font='alt'>
              <Grid size={ 12 } style={ styles.grid }>
                <Cell xs={ 12 } md={ 7 } style={ styles.leftColumnContainer }>
                  <Block style={ styles.availabilityPickerContainer }>
                    { this.isOlaAddonExperimentEnabled() && (
                      <AddonMenu staticContent={ staticContent } service={ selectedService } />
                    ) }
                    <AvailableTimePicker
                      fetchingComponent={ <Fetching fetching /> }
                      service={ selectedService }
                      timeZone={ timeZone }
                      maxFutureCalendarMonths={ maxFutureCalendarMonths }
                      dateTimeFormatter={ dateTimeFormatter }
                      section={ section }
                      setResource={ selectResource }
                      setAvailableTime={ availableTime =>
                        selectAvailableTime(availableTime, viewDevice)
                      }
                      fetchAvailableTimes={ fetchAvailableTimes }
                      fetchAvailableDates={ fetchAvailableDates }
                      onResourceClick={ this.onResourceClick }
                      onDayClick={ this.onDayClick }
                      onAvailableTimeClick={ this.onAvailableTimeClick }
                      i18n={{
                        morningHeader: staticContent.periodHeaderMorning,
                        afternoonHeader: staticContent.periodHeaderAfterNoon,
                        eveningHeader: staticContent.periodHeaderEvening,
                        expandPeriodCta: staticContent.seeMoreLink,
                        noAvailableTimesBody: staticContent.noAvailableTimes,
                        anyResourceHeader: staticContent.anyStaff,
                        anyResourceBody: staticContent.allBookingTimes,
                        ownerResourceHeader: staticContent.businessOwner,
                        expandResourceBodyCta: staticContent.more,
                        resourceSeparatorLine: staticContent.or,
                        errorBody: staticContent.errorTryAgain
                      }}
                    />
                  </Block>
                  { selectedAvailableTime && !this.isOlaBookButtonProminentExperimentEnabled() && (
                    <BookButtonContainer
                      onClick={ this.onClickBook }
                      disabled={ renderMode !== renderModes.PUBLISH }
                      style={ this.setBookButtonStyles(currentViewDevice) }
                      overrideOnClick={
                        !ConfigStore.isGopayCartOn() || selectedService.location_type === 'customer'
                      }
                      dataAid={ dataAids.AVAILABLE_TIME_SELECTION_BOOK_BUTTON }
                      navigate={ this.props.navigate }
                    >
                      { this.bookButtonText }
                    </BookButtonContainer>
                  ) }
                </Cell>
                <Cell xs={ 12 } md={ 5 }>
                  <HR style={ styles.separatorHorizontal } />
                  <ServiceInfo
                    service={ selectedService }
                    serviceStyles={ styles.serviceStyles }
                    staticContent={ staticContent }
                    onClickBook={ this.onClickBook }
                    renderMode={ renderMode }
                    renderModes={ renderModes }
                    bookButtonStyle={ this.setBookButtonStyles(currentViewDevice) }
                    bookButtonNavigate={ this.props.navigate }
                    bookButtonText={ this.bookButtonText }
                    isOlaServiceInViewport={ isInView }
                    selectedAvailableTime={ selectedAvailableTime }
                    depositShown
                    isDescriptionShown
                    isBookButtonShown={ this.isOlaBookButtonProminentExperimentEnabled() }
                    isOlaBookingServiceLocationExperimentEnabled={ this.isOlaBookingServiceLocationExperimentEnabled() }
                    isOlaStrikeThroughPriceExperimentEnabled={
                      isOlaStrikeThroughPriceExperimentEnabled
                    }
                    showAddonsInfo={
                      this.isOlaAddonExperimentEnabled() &&
                      this.isOlaAddonPhaseIIExperimentEnabled()
                    }
                    isDescriptionTrimmable
                    isImageShown
                  />
                  <HR style={ styles.separatorHorizontal } />
                  <SocialSharing
                    showFacebook={ showFacebook }
                    showTwitter={ showTwitter }
                    isPublishMode={ renderMode === renderModes.PUBLISH }
                    staticContent={ staticContent }
                  />
                </Cell>
              </Grid>
            </Block>
          </div>
        ) }
      </InViewportWrapper>
    );
  }
}

AvailableTimeSelection.propTypes = {
  renderMode: PropTypes.string,
  navigate: PropTypes.func,
  section: PropTypes.string,
  staticContent: PropTypes.object.isRequired,
  olaFbPixelId: PropTypes.string,
  vnextFbPixelId: PropTypes.string,
  viewDevice: PropTypes.string,
  showFacebook: PropTypes.bool,
  isOlaStrikeThroughPriceExperimentEnabled: PropTypes.bool,
  showTwitter: PropTypes.bool
};
